import React, { useRef, useState } from "react";
import axios from "axios";
import {
  ArrowUpRight, Mail, Phone, MapPin, Clock, Check,
  Instagram, Linkedin, Facebook,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import ContactHero from "@/components/hero/ContactHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = ({ onOpenAudit }) => {
  const { t, lang } = useLang();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [data, setData] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrMsg("");
    try {
      await axios.post(`${API}/leads/contact`, { ...data, lang }, { timeout: 15000 });
      setSubmitted(true);
      setData({ name: "", email: "", phone: "", service: "", message: "" });
      setTimeout(() => setSubmitted(false), 8000);
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || (lang === "bg" ? "Нещо се обърка." : "Something went wrong."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-brand-bg text-white pt-32">
      <ContactHero onOpenAudit={onOpenAudit} onScrollToForm={scrollToForm} />

      <section ref={formRef} className="border-b border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12">
          <div className="lg:col-span-5 lg:border-r border-brand-border lg:pr-10 py-16">
            <ul className="space-y-4">
              {[
                { Icon: Mail, label: t.contact.info.emailLabel, value: "contact@primecollective.bg", href: "mailto:contact@primecollective.bg" },
                { Icon: Phone, label: t.contact.info.phoneLabel, value: "+359 885 333 750", href: "tel:+359885333750" },
                { Icon: MapPin, label: t.contact.info.locationLabel, value: t.contact.info.location, href: null },
                { Icon: Clock, label: t.contact.info.hoursLabel, value: t.contact.info.hours, href: null },
              ].map(({ Icon, label, value, href }) => (
                <li key={label} className="flex items-start gap-5 group rounded-xl border border-brand-border p-5 hover:border-brand-purple/60 hover:bg-brand-surface/50 transition-all">
                  <span className="w-12 h-12 rounded-full grid place-items-center bg-gradient-to-br from-brand-purple/20 to-brand-pink/20 text-brand-purpleLight group-hover:from-brand-purple group-hover:to-brand-pink group-hover:text-white transition-colors shrink-0">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <div className="flex-1">
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-1">{label}</p>
                    {href ? (
                      <a href={href} className="text-base md:text-lg font-medium text-white hover:text-brand-purpleLight transition-colors">{value}</a>
                    ) : (
                      <p className="text-base md:text-lg font-medium">{value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 rounded-2xl border border-brand-border bg-brand-surface/60">
              <h4 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-white mb-4">
                {t.contact.form.whatYouGet}
              </h4>
              <ul className="space-y-3">
                {t.contact.form.perks.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-200">
                    <span className="mt-1 w-4 h-4 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink grid place-items-center shrink-0">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 flex items-center gap-3">
              {[
                { Icon: Instagram, href: "https://www.instagram.com/primecollective.bg/", label: "Instagram" },
                { Icon: Linkedin, href: "https://www.linkedin.com/company/primecollective", label: "LinkedIn" },
                { Icon: Facebook, href: "https://www.facebook.com/primecollective", label: "Facebook" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="w-11 h-11 grid place-items-center rounded-full border border-brand-border hover:border-brand-purple hover:text-white hover:bg-brand-purple/10 transition-all text-zinc-300"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-10 py-16">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-2">02</p>
            <h2 className="font-heading text-3xl md:text-5xl font-black uppercase leading-[0.95] tracking-tighter mb-3">
              {t.contact.form.title}
            </h2>
            <p className="text-zinc-400 mb-10">{t.contact.form.sub}</p>

            <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label={t.contact.form.name} required testId="contact-name" value={data.name} onChange={(v) => setData((d) => ({ ...d, name: v }))} />
                <Field label={t.contact.form.email} type="email" required testId="contact-email" value={data.email} onChange={(v) => setData((d) => ({ ...d, email: v }))} />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label={t.contact.form.phone} type="tel" testId="contact-phone" value={data.phone} onChange={(v) => setData((d) => ({ ...d, phone: v }))} />
                <SelectField label={t.contact.form.service} placeholder={t.contact.form.servicePlaceholder} options={t.contact.form.services} testId="contact-service" value={data.service} onChange={(v) => setData((d) => ({ ...d, service: v }))} />
              </div>
              <Field label={t.contact.form.message} required multiline testId="contact-message" value={data.message} onChange={(v) => setData((d) => ({ ...d, message: v }))} />

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center justify-center gap-2 btn-holo text-white font-bold uppercase tracking-wide px-8 py-5 rounded-full text-sm w-full sm:w-auto disabled:opacity-60"
              >
                {submitting ? (lang === "bg" ? "Изпращане..." : "Sending...") : t.contact.form.submit}
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </button>

              {submitted && (
                <div data-testid="contact-success" className="rounded-xl border border-brand-teal/50 bg-brand-teal/10 text-brand-tealLight p-4 text-sm font-mono">
                  ✓ {t.contact.form.success}
                </div>
              )}
              {errMsg && (
                <div data-testid="contact-error" className="rounded-xl border border-brand-pink/50 bg-brand-pink/10 text-brand-pinkLight p-4 text-sm font-mono">
                  ✗ {errMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, type = "text", required, multiline, value, onChange, testId }) => (
  <label className="block">
    <span className="block text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">
      {label} {required && <span className="text-brand-pink">*</span>}
    </span>
    {multiline ? (
      <textarea
        data-testid={testId}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        className="w-full bg-brand-surface/40 border border-brand-border focus:border-brand-purple focus:outline-none px-4 py-3 text-white placeholder-zinc-600 transition-colors rounded-xl resize-none"
      />
    ) : (
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-brand-surface/40 border border-brand-border focus:border-brand-purple focus:outline-none px-4 py-3 text-white placeholder-zinc-600 transition-colors rounded-xl"
      />
    )}
  </label>
);

const SelectField = ({ label, placeholder, options, value, onChange, testId }) => (
  <label className="block">
    <span className="block text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">{label}</span>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-brand-surface/40 border border-brand-border focus:border-brand-purple focus:outline-none px-4 py-3 text-white transition-colors rounded-xl appearance-none cursor-pointer"
    >
      <option value="" className="bg-brand-bg">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-brand-bg">{o}</option>
      ))}
    </select>
  </label>
);

export default Contact;
